import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { MapPin } from "lucide-react";
import { useLang } from "../i18n";

const PROJECTS = [
  {
    title: "Villa Residenziale Contemporanea",
    titleEn: "Contemporary Residential Villa",
    category: "Nuove Costruzioni",
    location: "Bologna Colli",
    url: "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGR1c2slMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzkyNDM5NDI3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Attico & Ristrutturazione Completa",
    titleEn: "Penthouse & Full Renovation",
    category: "Ristrutturazioni",
    location: "Centro Storico, Bologna",
    url: "https://images.pexels.com/photos/30211382/pexels-photo-30211382.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    title: "Complesso Direzionale a Impatto Zero",
    titleEn: "Zero-Impact Office Complex",
    category: "Impianti & Fotovoltaico",
    location: "Casalecchio di Reno (BO)",
    url: "https://images.unsplash.com/photo-1770391949357-3c27554d3a78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbnRlcmlvciUyMHJlbm92YXRpb24lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBzb2xhciUyMHBhbmVsJTIwYmx1ZXByaW50fGVufDB8fHx8MTc5MjQzOTQzNHww&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Restauro Edificio Storico",
    titleEn: "Historic Building Restoration",
    category: "Ristrutturazioni",
    location: "San Lazzaro di Savena (BO)",
    url: "https://images.pexels.com/photos/28054849/pexels-photo-28054849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const FILTERS = ["Tutti", "Nuove Costruzioni", "Ristrutturazioni", "Impianti & Fotovoltaico"];
const CAT_EN = {
  "Nuove Costruzioni": "New Builds",
  Ristrutturazioni: "Renovations",
  "Impianti & Fotovoltaico": "Systems & Solar",
};

export const Portfolio = () => {
  const [active, setActive] = useState("Tutti");
  const { t, lang } = useLang();
  const visible = active === "Tutti" ? PROJECTS : PROJECTS.filter((p) => p.category === active);
  const catLabel = (c) => (lang === "en" ? CAT_EN[c] || c : c);

  return (
    <section id="lavori" data-testid="portfolio-section" className="relative py-24 sm:py-32 bg-[#0A1220]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Eyebrow>{t("I nostri lavori", "Our projects")}</Eyebrow>
            <h2 className="mt-5 font-display font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              {t("Progetti realizzati", "Projects built")}{" "}
              <span className="font-serif italic font-medium text-gold-gradient">
                {t("con cura e attenzione", "with care and attention")}
              </span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`portfolio-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setActive(f)}
                className={`text-xs sm:text-sm px-4 py-2 rounded-full border transition-colors duration-300 ${
                  active === f
                    ? "bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] text-[#070D18] font-semibold border-transparent"
                    : "border-[#D4AF37]/30 text-slate-300 hover:border-[#D4AF37]/70 hover:text-[#F3E08B]"
                }`}
              >
                {f === "Tutti" ? t("Tutti", "All") : catLabel(f)}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.title}
                initial={{ opacity: 0, y: 32, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.55, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`portfolio-card-${i + 1}`}
                className="trace-card group relative overflow-hidden rounded-2xl"
              >
                <div className="relative h-72 sm:h-80 overflow-hidden">
                  <img
                    src={p.url}
                    alt={lang === "en" ? p.titleEn : p.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070D18] via-[#070D18]/25 to-transparent" />
                  <span className="absolute top-4 left-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#F3E08B] bg-[#070D18]/70 backdrop-blur-sm border border-[#D4AF37]/40 rounded-full px-3 py-1.5">
                    {catLabel(p.category)}
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-white">
                    {lang === "en" ? p.titleEn : p.title}
                  </h3>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-300">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    {p.location}
                  </p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
